import { useMemo, useState } from "react";
import { TrendingUp, Sparkles } from "lucide-react";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { cn } from "@/lib/utils";
import InfoTip from "./InfoTip";
import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";
import type { AnyRow } from "@/lib/rowTypes";

type Range = "4w" | "12w" | "90d";

interface Props {
  analytics: ParentAnalyticsResult;
  insight: string;
}

export default function LearningMomentumCard({ analytics, insight }: Props) {
  const [range, setRange] = useState<Range>("12w");

  const data = useMemo(() => {
    const days = range === "4w" ? 28 : range === "12w" ? 84 : 90;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const start = new Date(now);
    start.setDate(now.getDate() - days);

    // weekly buckets keyed by Monday
    type Bucket = { ts: number; lessons: number; quizzes: number };
    const map = new Map<number, Bucket>();
    const mondayOf = (d: Date) => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      const day = (x.getDay() + 6) % 7;
      x.setDate(x.getDate() - day);
      return x.getTime();
    };
    for (let i = 0; i <= days; i += 7) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const k = mondayOf(d);
      if (!map.has(k)) map.set(k, { ts: k, lessons: 0, quizzes: 0 });
    }
    for (const l of analytics.lessonActivity) {
      const d = new Date(l.date);
      if (d < start) continue;
      const k = mondayOf(d);
      const b = map.get(k) ?? { ts: k, lessons: 0, quizzes: 0 };
      b.lessons += 1;
      map.set(k, b);
    }
    for (const q of analytics.quizAttempts) {
      const d = new Date(q.completedAt);
      if (d < start) continue;
      const k = mondayOf(d);
      const b = map.get(k) ?? { ts: k, lessons: 0, quizzes: 0 };
      b.quizzes += 1;
      map.set(k, b);
    }
    return Array.from(map.values())
      .sort((a, b) => a.ts - b.ts)
      .map((b) => ({
        label: new Date(b.ts).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        lessons: b.lessons,
        quizzes: b.quizzes,
      }));
  }, [analytics, range]);

  const empty = data.every((d) => d.lessons === 0 && d.quizzes === 0);
  const totalEvents = data.reduce((s, d) => s + d.lessons + d.quizzes, 0);
  const sparse = !empty && totalEvents < 6;
  const chartHeight = sparse ? 150 : 200;

  return (
    <section className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-card/80 via-card/60 to-card/40 p-5 backdrop-blur-2xl shadow-[0_24px_60px_-30px_hsl(var(--primary)/0.45),inset_0_1px_0_0_hsl(var(--foreground)/0.05)] sm:p-6">
      <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
      <div className="relative">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
                <TrendingUp className="h-3.5 w-3.5" />
              </span>
              <h3 className="font-heading text-base font-semibold tracking-tight text-foreground sm:text-lg">Learning Momentum</h3>
              <InfoTip
                title="Learning Momentum"
                body="Weekly lessons and quizzes completed. A flat week is a re-engagement signal; rising bars mean active learning."
              />
            </div>
            <p className="mt-2 max-w-md text-xs leading-relaxed text-muted-foreground sm:text-sm">
              {sparse
                ? "Early signal: activity is starting to build. Trend confidence improves after more lessons and decision checks."
                : insight}
            </p>
            {sparse && (
              <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[10.5px] font-medium text-primary">
                <Sparkles className="h-3 w-3" />
                Early signal
              </span>
            )}
            <div className="mt-3 flex items-center gap-3 text-[11px] text-muted-foreground">
              <Legend color="hsl(var(--primary))" label="Lessons" />
              <Legend color="hsl(var(--secondary))" label="Quizzes" />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 rounded-full border border-white/[0.06] bg-background/40 p-1 text-[11px]">
            {(["4w", "12w", "90d"] as Range[]).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={cn("rounded-full px-2.5 py-1 font-medium transition", range === r ? "bg-primary/20 text-primary" : "text-muted-foreground hover:text-foreground")}
              >
                {r === "4w" ? "Past 4 Weeks" : r === "12w" ? "Past 12 Weeks" : "Last 90 Days"}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-white/[0.04] bg-background/20 p-3">
          {empty ? (
            <div className="flex h-44 items-center justify-center text-xs text-muted-foreground">No activity in this range yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={chartHeight}>
              <LineChart data={data} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--foreground)/0.05)" strokeDasharray="2 6" vertical={false} />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis allowDecimals={false} stroke="hsl(var(--muted-foreground))" fontSize={10} tickLine={false} axisLine={false} width={28} />
                <Tooltip
                  cursor={{ stroke: "hsl(var(--primary)/0.3)" }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const lessons = (payload.find((p: AnyRow) => p.dataKey === "lessons")?.value as number) ?? 0;
                    const quizzes = (payload.find((p: AnyRow) => p.dataKey === "quizzes")?.value as number) ?? 0;
                    const interp =
                      lessons === 0 && quizzes === 0 ? "Momentum is currently low."
                      : lessons > 0 && quizzes > 0 ? "Active learning + assessment."
                      : lessons > 0 ? "Lesson-only week."
                      : "Quiz-only week.";
                    return (
                      <div className="rounded-xl border border-primary/25 bg-card/90 px-3 py-2 text-xs backdrop-blur-xl">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Week of {label}</div>
                        <div className="mt-1 text-foreground">{lessons} lesson{lessons === 1 ? "" : "s"}, {quizzes} quiz{quizzes === 1 ? "" : "zes"}</div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">{interp}</div>
                      </div>
                    );
                  }}
                />
                <Line type="monotone" dataKey="lessons" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 3, fill: "hsl(var(--primary))" }} />
                <Line type="monotone" dataKey="quizzes" stroke="hsl(var(--secondary))" strokeWidth={2.5} dot={{ r: 3, fill: "hsl(var(--secondary))" }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </section>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-1.5 w-3 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}